import React from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { analyticsIcon, aiIcon, reportIcon, layers } from "layouts/LeaderLayout/Icons";
import { useSelector, shallowEqual } from "react-redux";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeUp} 280ms ease-out both;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol', 'Noto Color Emoji', sans-serif;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Hero = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 32px 0 10px;
  min-height: 150px;
`;

const HeroBackdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 100vw; /* full-bleed */
  transform: translateX(-50%);
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(1200px 500px at 10% -10%, rgba(255, 182, 141, 0.28), transparent 60%),
              radial-gradient(1000px 420px at 90% -20%, rgba(123, 97, 255, 0.22), transparent 60%),
              radial-gradient(900px 380px at 50% -10%, rgba(44, 107, 237, 0.20), transparent 60%);
  filter: saturate(110%);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 38px;
  line-height: 1.15;
  font-weight: 700;
  color: #111111;
  letter-spacing: -0.02em;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -10px;
    width: 72px;
    height: 3px;
    background: linear-gradient(90deg, #2c6bed, #6b5cff);
    border-radius: 2px;
    opacity: 0.9;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 16px;
  color: #4a4a4a;
  max-width: 720px;
`;

const SearchWrap = styled.form`
  margin-top: 8px;
  max-width: 680px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
`;

const SearchInput = styled.input`
  height: 46px;
  border-radius: 999px;
  border: 1px solid #e7e7e7;
  padding: 0 16px 0 16px;
  font-size: 15px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  transition: border-color 200ms ease, box-shadow 200ms ease;
  &:focus {
    border-color: #b9c8ff;
    box-shadow: 0 0 0 3px rgba(44, 107, 237, 0.18);
    outline: none;
  }
`;

const SearchButton = styled.button`
  height: 46px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 200ms ease, opacity 200ms ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(44, 107, 237, 0.25); }
  &:active { transform: translateY(0); }
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(6, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  grid-column: span 3;
  outline: none;
  cursor: pointer;

  @media (max-width: 1080px) {
    grid-column: span 6;
  }
  @media (max-width: 640px) {
    grid-column: span 1;
  }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 18px;
  background: #ffffff;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
  transition: box-shadow 240ms ease, transform 240ms ease, border-color 240ms ease;
  min-height: 160px;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #dcdcdc;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }

  &:focus-within {
    border-color: #2c6bed;
    box-shadow: 0 0 0 3px rgba(44, 107, 237, 0.18);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(1200px 400px at -10% -10%, rgba(44,107,237,0.06), transparent 60%);
    opacity: 0;
    transition: opacity 240ms ease;
  }
  &:hover::after { opacity: 1; }
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: #f7f9ff;
  border: 1px solid #e6ecff;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #161616;
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5a5a5a;
`;

const CTA = styled.span`
  margin-top: 6px;
  font-size: 14px;
  color: #2c6bed;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const itemFade = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled(Card)`
  animation: ${itemFade} 360ms ease-out both;
  animation-delay: ${(p) => (p.delayMs ? `${p.delayMs}ms` : "0ms")};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

function ActionIcon({ type, color }) {
  const c = color || "#64748b";
  switch (type) {
    case "analytics":
      return analyticsIcon(c);
    case "nora":
      return aiIcon(c);
    case "pulse":
      return layers(c);
    case "reports":
      return reportIcon(c);
    default:
      return analyticsIcon(c);
  }
}

const actions = [
  {
    key: "analytics",
    title: "Analyze data",
    description: "Explore dashboards and uncover insights in your datasets.",
    to: "/leader/audit/new-analytics",
    color: "#2c6bed",
  },
  {
    key: "nora",
    title: "Chat with Nora",
    description:
      "Ask questions, summarize findings, and get guidance instantly.",
    to: "/leader/audit/AI/copilot",
    color: "#6b5cff",
  },
  {
    key: "pulse",
    title: "Create a pulse survey",
    description: "Quickly launch a pulse to capture timely feedback.",
    to: "/leader/audit/schedule-v2/pulse",
    color: "#22a06b",
  },
  {
    key: "reports",
    title: "Review or generate a report",
    description: "Automate recurring reports or create one on demand.",
    to: "/leader/audit/reports",
    color: "#d66b00",
  },
];

const HomeLanding = () => {
  const navigate = useNavigate();
  // Pull current employee and permissions
  const { EmployeeId, employee, employeePermission } = useSelector(
    (state) => ({
      EmployeeId: state.auth?.employee_id,
      employee: state.employees?.userEmp,
      employeePermission: state.employee_permission,
    }),
    shallowEqual
  );

  const isAdmin = employee?.account_type === 5;

  const hasEmployeeAuthDetail = (key) => {
    try {
      const list = employeePermission?.employee_permission || [];
      const record = list.find((p) => p?.employee_id === Number(EmployeeId));
      const details = record?.auth_details || record?.details || [];
      return details?.some(
        (d) => d?.name === key || d?.name_id === key || d?.value === key
      );
    } catch (e) {
      return false;
    }
  };

  // Check Nora access with backward compatibility and support for explicit 'false' value
  const hasNoraAccess = () => {
    if (isAdmin) return true;
    if (employee?.access_data_analytics !== 1) return false;
    
    try {
      const list = employeePermission?.employee_permission || [];
      const record = list.find((p) => p?.employee_id === Number(EmployeeId));
      const details = record?.auth_details || record?.details || [];
      
      // Backward compatibility: if no permission records, default to enabled
      if (!list.length || !record || !details.length) {
        return true;
      }
      
      // Find nora_ai detail
      const noraDetail = details.find(
        (d) => d?.name === 'nora_ai' || d?.name_id === 'nora_ai' || d?.value === 'nora_ai'
      );
      
      // Backward compatibility: if doesn't exist, default to enabled
      if (!noraDetail) return true;
      
      // If explicitly set to false, deny access
      if (noraDetail?.response === false || noraDetail?.value === 'false') return false;
      
      // Otherwise, grant access
      return true;
    } catch (e) {
      return true; // Backward compatibility: default to enabled on error
    }
  };

  const canAccess = {
    analytics: isAdmin || employee?.access_data_analytics === 1,
    nora: hasNoraAccess(),
    pulse: isAdmin || employee?.survey_schedule === 1,
    reports:
      isAdmin ||
      hasEmployeeAuthDetail('pdf') ||
      hasEmployeeAuthDetail('pdf_enabled') ||
      hasEmployeeAuthDetail('PDF - Enabled'),
  };

  const visibleActions = actions.filter((a) => canAccess[a.key]);
  function onSearchSubmit(e) {
    e.preventDefault();
    navigate("/leader/audit/AI/copilot");
  }

  return (
    <Page>
        <HeroBackdrop />
      <Hero>
        
        <Title>Turn insight into action</Title>
        <Subtitle>
          Explore analytics, chat with Nora, launch pulse surveys, or generate reports.
        </Subtitle>
        {/* <SearchWrap onSubmit={onSearchSubmit} aria-label="Ask Nora">
          <SearchInput placeholder="Ask Nora anything…" aria-label="Ask Nora anything" />
          <SearchButton type="submit">Ask Nora</SearchButton>
        </SearchWrap> */}
      </Hero>

      <Grid>
        {visibleActions.map((action, idx) => {
          const titleId = `home-card-title-${action.key}`;
          const descId = `home-card-desc-${action.key}`;
          return (
            <CardLink
              key={action.key}
              to={action.to}
              aria-labelledby={titleId}
              aria-describedby={descId}
            >
              <AnimatedCard delayMs={idx * 60}>
              <IconWrap>
                  <ActionIcon type={action.key} color={action.color} />
              </IconWrap>
              <CardTitle id={titleId}>{action.title}</CardTitle>
              <CardDesc id={descId}>{action.description}</CardDesc>
              <CTA>
                Open
                <span style={{ display: 'inline-flex', transform: 'translateX(0)', transition: 'transform 200ms ease' }} className="arrow-wrap">
                  <Arrow />
                </span>
              </CTA>
              </AnimatedCard>
            </CardLink>
          );
        })}
      </Grid>
      <AfterGridNote>
        <NoteLink to="/leader/audit/data-access">What data do I have access to?</NoteLink>
      </AfterGridNote>
    </Page>
  );
};

export default HomeLanding;


const AfterGridNote = styled.div`
  margin-top: -14px;
  display: flex;
  justify-content: flex-end;
`;

const NoteLink = styled(Link)`
  color: #2c6bed;
  font-size: 14px;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
